<template>
  <ClientOnly>
    <div class="container reservationRibbon__Wrapper" ref="reservationRibbon">

      <div class="reservationRibbon">
        <button class="reservationRibbon__MobileButton" @click="openReservationPanel" :aria-label="$t('search')" />
        <div class="reservationRibbon__Accommodation">
          <button type="button" @click="openReservationPanel(0, $event)">
            {{ (booking?.accommodation?.accommodation?.name_short || booking?.accommodation?.accommodation?.name) || (booking?.accommodation?.name_short || booking?.accommodation?.name) || $t('reservation.allAccommodations') }}
          </button>
        </div>
        <div class="reservationRibbon__Dates">
          <button type="button" @click="openReservationPanel(1)">
            {{ formatDate(booking.dates[0]) }} - {{ formatDate(booking.dates[1]) }}
          </button>
        </div>
        <div class="reservationRibbon__Guests">
          <button type="button" @click="openReservationPanel(2)">
            <span>{{ booking.adults_count }} {{ $t('reservation.adults') }}</span>
            <span v-if="booking.children">&nbsp; {{ booking.children }} {{ $t('reservation.children') }}</span>
          </button>
        </div>
        <div class="reservationRibbon__Search">
          <button type="button" @click="openReservationPanel(0, $event)">
            <Icon name="search" />
          </button>
        </div>
      </div>
    </div>

    <div v-show="isReservationPanelActive" class="reservationPanel">
      <div :class="{ isActive: isReservationPanelActive }">
        <h2 v-text="$t('reservation.search')" />
        <div class="reservationPanel__ActiveRibbon">
          <div class="reservationPanel__Accommodation" :class="{ isActive: activeSection === SECTION_TYPES[0] }">
            <img src="/resources/images/icons/location.svg" width="24" height="24" />
            <input
              type="text"
              ref="searchInput"
              v-model="searchInputValue"
              :placeholder="searchPlaceholder"
              @focus="toggleAccommodationStyle"
              @blur="toggleAccommodationStyle"
            />
            <button type="button" v-if="searchInputValue.length > 0" @click="clearSearchInput()">
              <Icon name="clear" />
              <span>Clear</span>
            </button>
          </div>

          <div class="reservationPanel__Dates" :class="{ isActive: activeSection === SECTION_TYPES[1] }">
            <button type="button" @click="setActiveSection(1)">
              <img src="/resources/images/icons/calendar.svg" width="24" height="24" />
              {{ formatDatepickerDate(booking.dates) }}
            </button>
          </div>

          <div class="reservationPanel__Guests" :class="{ isActive: activeSection === SECTION_TYPES[2] }">
            <button type="button" @click="setActiveSection(2)">
              <img src="/resources/images/icons/guests.svg" width="24" height="24" />
              <span>{{ booking.adults_count }} {{ $t('reservation.adults') }}</span>
              <span v-if="booking.children">{{ booking.children }} {{ $t('reservation.children') }}</span>
            </button>
          </div>

          <div class="reservationPanel__Code" :class="{ isActive: activeSection === SECTION_TYPES[3] }">
            <TextField
              v-model="booking.promo_code"
              :placeholder="$t('reservation.codePlaceholder')"
              @focus="setActiveSection(3)"
            >
              <template #prepend>
                <img src="/resources/images/icons/discount.svg" width="24" height="24" />
              </template>
            </TextField>
          </div>

          <div class="reservationPanel__Search">
            <Button @click="startSearch">{{ $t('checkRates') }}</Button>
          </div>
        </div>

        <div class="container reservationPanel__SectionsWrapper" ref="mobileAccommodations">
          <section v-if="activeSection === SECTION_TYPES[0]">
            <div class="reservationPanel__AccommodationsDesktop">
              <transition name="fade" mode="out-in">
                <ReservationSearchResults
                  v-if="isSearchResultVisible"
                  :data="searchResults"
                  @set-location="setLocation"
                />
                <div v-else class="reservationPanel__AccommodationsWrapper">
                  <div v-for="category in ACCOMMODATION_TYPES">
                    <div class="reservationPanel__AccommodationTop">
                      <template v-if="category === ACCOMMODATION_TYPES[0]">
                        <Icon name="location" />
                      </template>
                      <template v-else-if="category === ACCOMMODATION_TYPES[1]">
                        <Icon name="hotel" />
                      </template>
                      <template v-else>
                        <Icon name="campsite" />
                      </template>
                      <span v-text="$t(`reservation.${category}`)" />
                    </div>
                    <Button
                      secondary
                      small
                      @click="getAccommodationGroupLink(category)"
                    >
                      {{ $t('reservation.searchAll') }}
                    </Button>
                    <div class="reservationPanel__AccommodationsListing">
                      <span>
                        <template v-if="category === ACCOMMODATION_TYPES[1]">
                          {{ $t('reservation.featuredHotels') }}
                        </template>
                        <template v-else-if="category === ACCOMMODATION_TYPES[2]">
                          {{ $t('reservation.featuredCamps') }}
                        </template>
                      </span>
                      <template v-if="category === ACCOMMODATION_TYPES[0]">
                        <button v-for="destination in destinations.places" @click="setLocation(destination)">
                          <span>{{ destination.name }}</span>
                        </button>
                      </template>
                      <template v-else-if="category === ACCOMMODATION_TYPES[1]">
                        <button v-for="hotel in destinations.hotels" @click="setLocation(hotel.accommodation || {})">
                          <span>{{ hotel.accommodation.name_short || hotel.accommodation.name }}</span>
                          <span v-if="hotel.location?.name" class="reservationPanel__ItemLocation">
                            {{ hotel.location?.name }}
                          </span>
                        </button>
                      </template>
                      <template v-else-if="category === ACCOMMODATION_TYPES[2]">
                        <button v-for="camp in destinations.camps" @click="setLocation(camp.accommodation || {})">
                          <span>{{ camp.accommodation.name_short || camp.accommodation.name }}</span>
                          <span v-if="camp.location?.name" class="reservationPanel__ItemLocation">
                            {{ camp.location?.name }}
                          </span>
                        </button>
                      </template>
                    </div>
                  </div>
                </div>
              </transition>
            </div>

            <ReservationPanelMobileHeader @stepBack="returnFromAccommodations" @close="closePanel">
              <div class="reservationPanel__AccommodationInput">
                <input
                  type="text"
                  v-model="searchInputValue"
                  :placeholder="searchPlaceholder"
                  @focus="toggleAccommodationStyle"
                  @blur="toggleAccommodationStyle"
                />
                <img src="/resources/images/icons/location.svg" width="24" height="24" />
                <button type="button" v-if="searchInputValue.length > 0" @click="clearSearchInput()" class="reservationPanel__AccomodationInputClear--Mobile">
                  <Icon name="clear" />
                  <span>Clear</span>
                </button>
              </div>
            </ReservationPanelMobileHeader>

            <div class="reservationPanel__AccommodationsMobile">
              <transition name="fade">
                <ReservationSearchResults
                  v-if="isSearchResultVisible"
                  :data="searchResults"
                  @set-location="setLocation"
                />
                <div v-else class="reservationPanel__MobileAccommodationsList">
                  <div v-for="destination in destinations.places">
                    <button
                      type="button"
                      @click="setLocation(destination)"
                      class="reservationPanel__DestinationButton reservationPanel__ResultButton"
                    >
                      <img src="/resources/images/icons/location.svg" width="24" height="24" />
                      {{ destination.name }}
                    </button>
                    <button
                      v-for="hotel in destination.accommodations.filter(item => hotelIdentificators.includes(item.type))"
                      @click="setLocation(hotel)"
                      class="reservationPanel__ResultButton"
                    >
                      <img src="/resources/images/icons/hotel.svg" width="24" height="24" />
                      {{ hotel.name }}
                    </button>
                    <button
                      v-for="camp in destination.accommodations.filter(item => !hotelIdentificators.includes(item.type))"
                      @click="setLocation(camp)"
                      class="reservationPanel__ResultButton"
                    >
                      <img src="/resources/images/icons/campsite.svg" width="24" height="24" />
                      {{ camp.name }}
                    </button>
                  </div>
                </div>
              </transition>

              <transition name="fade">
                <div
                  v-if="!searchInputValue.length || searchInputValue === $t('reservation.all.destinations')"
                  class="reservationPanel__BottomButtonWrapper"
                >
                  <Button
                    secondary
                    medium
                    @click="setLocation({ id: 'destinations', name: $t('reservation.all.destinations'), group: true })"
                  >
                    {{ $t('reservation.allAccommodation') }} ({{ numberOfResults }})
                  </Button>
                </div>
              </transition>
            </div>
          </section>

          <section
            v-else-if="activeSection === SECTION_TYPES[1]"
            class="reservationPanel__DateSection"
            :class="{ withDateTabs: booking.accommodation }"
          >
            <ReservationPanelMobileHeader @stepBack="returnFromAccommodations" @close="closePanel">
              <!-- <div v-if="booking.accommodation" class="reservationPanel__TabsWrapper mobile">
                <button
                  v-for="type in DURATION_TYPES"
                  @click="changeDurationType(type)"
                  :class="{ isActive: booking.duration_type === type }"
                >
                  {{ $t(`reservation.${type}`) }}
                </button>
              </div> -->
              <div v-show="booking.duration_type === 'calendar'" class="reservationPanel__DayNames">
                <span v-for="day in DAYS">{{ $t(`days.${day}`) }}</span>
              </div>
            </ReservationPanelMobileHeader>
            <!-- <div v-if="booking.accommodation" class="reservationPanel__TabsWrapper">
              <button
                v-for="type in DURATION_TYPES"
                @click="changeDurationType(type)"
                :class="{ isActive: booking.duration_type === type }"
              >
                {{ $t(`reservation.${type}`) }}
              </button>
            </div> -->

            <template v-if="booking.duration_type === 'calendar'">
              <VueDatePicker
                :locale="lang"
                class="reservationPanel__Calendar"
                v-model="booking.dates"
                range
                :month-change-on-scroll="!isMobile"
                :multi-calendars="{ count: isMobile ? 24 : 2 }"
                :clearable="false"
                :auto-apply="true"
                :enable-time-picker="false"
                month-name-format="long"
                :format="formatDatepickerDate"
                :hide-offset-dates="true"
                :min-date="new Date()"
                :max-date="twoYearsFromToday()"
                inline
                :disabled-dates="disableDate"
                @update:model-value="updateDates"
              >
                <template #arrow-left>
                  <Icon name="arrow-left-circle"/>
                </template>
                <template #input-icon>
                  <img src="/resources/images/icons/calendar.svg" width="24" height="24" />
                </template>
                <template #day="{ day }">
                  <div class="dp__cell__dayValue">
                    {{ day }}
                  </div>
                </template>
                <template #arrow-right>
                  <Icon name="arrow-right-circle" />
                </template>
              </VueDatePicker>
            </template>

            <div v-else class="reservationPanel__Duration">
              <h3 v-text="$t('reservation.stayDuration')" />
              <FieldGroup inline>
                <RadioField
                  v-for="item in STAY_OPTIONS"
                  :label="item.title"
                  :value="item.value"
                  v-model="booking.duration"
                />
              </FieldGroup>

              <h3 v-text="$t('reservation.startDate')" />
              <FieldGroup inline class="reservationPanel__MonthsSelector">
                <RadioField
                  v-for="item in VACATION_OPTIONS"
                  class="reservationPanel__MonthRadioField"
                  :label="item.title"
                  :value="item.value"
                  v-model="booking.month"
                >
                  <img src="/resources/images/icons/calendar.svg" width="24" height="24" />
                  <span>{{ item.title }}</span>
                  <span>{{ item.year }}</span>
                </RadioField>
              </FieldGroup>
            </div>

            <div class="reservationPanel__MobileConfirmButton">
              <Button @click="startSearch">{{ $t('reservation.confirm') }}</Button>
            </div>
          </section>

          <section v-else-if="activeSection === SECTION_TYPES[2]">
            <ReservationPanelMobileHeader @stepBack="returnFromAccommodations" @close="closePanel" />
            <div class="reservationPanel__GuestsFields">
              <CounterField
                :label="$t('reservation.adults')"
                v-model="booking.adults_count"
                :maxValue="10"
                :minValue="1"
                inline
              ></CounterField>
              <CounterField
                :label="$t('reservation.children')"
                v-model="booking.children"
                :maxValue="10"
                :minValue="0"
                inline
              ></CounterField>
              <TextField
                v-for="(child, index) in booking.children"
                :label="`${$t('reservation.childAge')} ${index + 1}`"
                v-model="booking.children_age[index]"
                :value="booking.children_age[index] || 0"
                :validate="{ type: ['number', 'min', 'max'], min: 0, max: 14, message: { max: $t('formErrors.childrenMaxAge') } }"
                min="0"
                type="number"
                grid
              />
            </div>
            <div class="reservationPanel__MobileConfirmButton">
              <Button @click="startSearch">{{ $t('reservation.confirm') }}</Button>
            </div>
          </section>
        </div>

        <button type="button" @click="closePanel" class="reservationPanel__CloseButton">
          {{ $t('close') }}
          <Icon name="close" />
        </button>
      </div>
    </div>
  </ClientOnly>
</template>

<script setup>
  import { add, isSameDay, differenceInDays, format, eachDayOfInterval } from 'date-fns'
  import VueDatePicker from '@vuepic/vue-datepicker'
  const { t } = useI18n();
  const { query } = useRoute();
  const { template } = usePage()
  const { lang } = useLanguage();
  const { link, accommodationsData } = useSetup();
  const { initialData: { value: {accommodation_kind_groups: accommodationKindGroups}}, aggregationPagesLinks } = useSetup();
  const { booking, destinations, submitSearchData } = useBooking()
  const { isReservationPanelActive, activeSection, SECTION_TYPES, openPanel, closePanel, setActiveSection } = useReservationPanel();

  const props = defineProps({
    sticky: {
      type: Boolean,
      default: false
    }
  })
  const emit = defineEmits(['search'])

  // Refs
  const searchInput = ref(null)
  const mobileAccommodations = ref(null)
  const reservationRibbon = ref(null)
  const accommodationAvailableDates = ref(null);

  let scrollTimeout

  const isMobile = ref(false)
  const searchResults = ref({})
  const searchPlaceholder = ref(t('reservation.findYourStay'))
  const searchInputValue = ref('')
  const hotelIdentificators = accommodationKindGroups.find(item => item.items.includes('HOTEL')).items;

  const DURATION_TYPES = ['calendar', 'flexible']
  const ACCOMMODATION_TYPES = ['destinations', 'hotel', 'camp']
  const DAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun']

  const isSearchResultVisible = computed(() => {
    return booking.value?.accommodation && !ACCOMMODATION_TYPES.includes(booking.value.accommodation.id) && searchInputValue?.value?.length > 0 && !['destinations', 'hotel', 'camp'].includes(booking.value.accommodation.id)
  });

  const numberOfResults = computed(() =>
    ['places', 'hotels', 'camps'].reduce((t, i) => (t += destinations.value[i]?.length), 0)
  );

  // used for disable dates in vuedatepicker
  // check if selectedAccommodation has availability_calendar and return all dates from range
  function collectAccommodationAvailableDates() {
    if (booking.value?.accommodation?.id) {
      let selectedAccommodation = accommodationsData.value[booking.value.accommodation.id];
      if (!selectedAccommodation?.availability_calendar) return null;
      try {
        return getAllDatesFromRange(selectedAccommodation.availability_calendar);
      } catch(error) {
        return null;
      }
    }
    return null;
  };

  // vuedatepicker can call method on the disabled-dates prop for every date visible in calendar.
  // For every date we must return true if date is disabled
  function disableDate(date) {
    if (!accommodationAvailableDates.value) return false;
    return !accommodationAvailableDates.value.find(item => isSameDay(item, date));
  }

  // get all dates from accommodation availability_calendar and set them in one array
  function getAllDatesFromRange(range) {
    let dates = [];
    if (!range.length) return [];
    for (let item of range) {
      dates = [...dates, ...eachDayOfInterval({ start: new Date(item.date_from), end: new Date(item.date_to) })]
    }
    return dates;
  }

  async function getAccommodationGroupLink(category) {
    let path = link('AccommodationList');
    if (category === 'hotel') path = aggregationPagesLinks.value.find(item => item.isHotel).sef || '';
    else if (category === 'camp') path = aggregationPagesLinks.value.find(item => !item.isHotel).sef || '';

    if (category) booking.value.accommodation = { id: category, name: t(`reservation.all.${category}`) }
    delete booking.value.accommodation_id;
    delete booking.value.place_id;
    await navigateTo(path);
    closePanel();
  }

  function handleResize() {
    isMobile.value = window.innerWidth <= 768
  }

  function handleScroll() {
    clearTimeout(scrollTimeout)
    scrollTimeout = setTimeout(() => {
      reservationRibbon.value.classList[reservationRibbon.value.getBoundingClientRect().top <= 0 ? 'add' : 'remove'](
        'reservationRibbon__Wrapper--Sticky'
      )
    }, 100)
  }

  function changeDurationType(type) {
    booking.value.duration_type = type
  }

  function openReservationPanel(section, e) {
    openPanel(section);
    if (section === 0 && searchInputValue.value) handleSearch(searchInputValue.value)
    if (e && searchInput.value)
      nextTick(() => {
        searchInput.value.value = ''
        searchInput.value.focus()
      })
  }

  function formatDatepickerDate(date) {
    return `${formatDate(date[0])} - ${formatDate(date[1])}`
  }

  function updateDates(dates) {
    const jsDates = dates.map(date => format(date, 'yyyy-MM-dd'));
    booking.value.dates = jsDates;
    if (isSameDay(jsDates[0], jsDates[1])) booking.value.dates = [jsDates[0], add(jsDates[1], { days: 1 })]
    if (differenceInDays(jsDates[1], jsDates[0]) > 90) booking.value.dates = [jsDates[0], add(jsDates[0], { days: 90 })]

    booking.value = {
      ...booking.value,
      date_from: booking.value.dates[0],
      date_to: booking.value.dates[1]
    }
  }

  function toggleAccommodationStyle(e) {
    if (e.type === 'focus') setActiveSection(0)
    searchPlaceholder.value = t(`reservation.${e.type === 'focus' ? 'destinationOrHotel' : 'findYourStay'}`)
  }

  async function setLocation(location) {
    let _booking = JSON.parse(JSON.stringify(booking.value))
    let _query = JSON.parse(JSON.stringify(query));
    // for group search we need to remove place_id and accommodation_id and send only id
    if (location.group) {
      delete _booking.place_id;
      delete _booking.accommodation_id;
      delete _query.place_id;
      delete _query.accommodation_id;
    } else {
      if (location.accommodations) {
        delete _booking.accommodation_id
        delete _query.accommodation_id;
        _booking.place_id = location.id
      } else {
        delete _booking.place_id
        _booking.accommodation_id = location.id
        delete _query.place_id;
      }
    }

    booking.value = {
      ..._booking,
      accommodation: JSON.parse(JSON.stringify(location))
    }

    if (!location.group) await useRouter().replace({ query: _query });

    searchInputValue.value = location.accommodation?.name ? (location.accommodation.name_short || location.accommodation.name) : (location.name_short || location.name)
    // To prevent watch booking.value.dates to trigger
    setTimeout(() => {
      setActiveSection(1)
    }, 1)
  }

  function returnFromAccommodations() {
    setActiveSection(null)
    if (!booking.value.accommodation) searchInputValue.value = ''
  }

  function handleSearch(value) {
    if (value?.length > 1) {
      searchResults.value = destinations.value.places
        .reduce((acc, nextItem) => {
          if ((nextItem.name_short || nextItem.name).toLowerCase().includes(value.toLowerCase())) acc.push(nextItem)

          if (nextItem.accommodations) {
            for (let item of nextItem.accommodations) {
              if ((item.name_short || item.name).toLowerCase().includes(value.toLowerCase())) {
                acc.push(item)
              }
            }
          }
          return acc
        }, [])
        .map(item => {
          item.displayTitle = (item.name_short || item.name).replace(new RegExp(value, 'gi'), match => `<strong>${match}</strong>`)
          return item
        })
    } else searchResults.value = []
  }

  function clearBookingData() {
    booking.value.rate_external_id = '';
    booking.value.package_external_id = '';
    booking.value.selected_units = [];
  }

  function clearSearchInput(){
    searchInputValue.value = '';
    if (activeSection.value !== SECTION_TYPES[0]) activeSection.value = SECTION_TYPES[0]
  }

  async function startSearch() {
    clearBookingData();
    const _childrenAge = booking.value.children_age.filter(item => !['', null].includes(item));
    booking.value.children_age = _childrenAge;
    booking.value.children = _childrenAge.length;
    if (booking.value.children_age.length) {
      booking.value.children_age = booking.value.children_age.map(item => parseInt(item) > 14 ? 14 : item)
    }
    if (booking.value?.accommodation && ['destinations', 'hotel', 'camp'].includes(booking.value.accommodation.id) || booking.value?.accommodation?.id === '') {
      getAccommodationGroupLink(booking.value.accommodation.id);
      emit('search');
    } else {
      const _template = await submitSearchData();
      if(template.value === _template) emit('search')
      closePanel();
    }
  }

  onMounted(() => {
    if (booking.value.accommodation?.accommodation) {
      searchInputValue.value = booking.value.accommodation.accommodation.name_short || booking.value.accommodation.accommodation.name;
    } else if (booking.value?.accommodation) {
      searchInputValue.value = booking.value.accommodation.name_short || booking.value.accommodation.name;
    }

    accommodationAvailableDates.value = collectAccommodationAvailableDates();
    setTimeout(() => {
      handleResize()
    }, 1);
    window.addEventListener('resize', handleResize)
    if (props.sticky) window.addEventListener('scroll', handleScroll)
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', handleResize)
    if (props.sticky) window.removeEventListener('scroll', handleScroll)
  })

  watch(() => searchInputValue.value, handleSearch)

  watch(
    () => booking.value.dates,
    () => {
      if (activeSection.value === SECTION_TYPES[1] && booking.value.dates[0] && booking.value.dates[1]) {
        setActiveSection(2)
      }
    }
  )

  watch(() => booking.value.accommodation, () => {
    searchInputValue.value = booking.value.accommodation.name_short || booking.value.accommodation.name
    accommodationAvailableDates.value = collectAccommodationAvailableDates();
  })

  watch(() => booking.value.children, () => {
    booking.value.children_age = booking.value.children_age.slice(0, booking.value.children)
  })

  // const STAY_OPTIONS = [
  //   { title: 'Weekend', value: 'Weekend' },
  //   { title: 'Week', value: 'Week' },
  //   { title: '10 days', value: '10 days' }
  // ]

  // const VACATION_OPTIONS = [
  //   { title: 'May', value: 'May', year: '2024' },
  //   { title: 'June', value: 'June', year: '2024' },
  //   { title: 'July', value: 'July', year: '2024' },
  //   { title: 'August', value: 'August', year: '2024' },
  //   { title: 'September', value: 'September', year: '2024' }
  // ]
</script>

<style lang="scss">
  @import url('@/assets/styles/components/_reservationPanel.scss');
  @import url('@/assets/styles/components/_datePicker.scss');
</style>
